import React, { useEffect, useState } from 'react';
import { func, object, string } from 'prop-types';
import { ModalStyled, ButtonStyled, ModalActionsStyled } from '@nike/nike-design-system-components';
import { useIdleTimer } from 'react-idle-timer';
import { useOktaAuth } from '@okta/okta-react';
import { connect } from 'react-redux';

import { getUserData } from '../../user/selectors';
import { setUserLogin } from '../../auth/actions';

import { currentOutage } from '../../notifications/reducer';

import { setGlobalHeader } from '../../util/http';
import { ONE_SECOND, ONE_MINUTE } from '../../util/constants';
import logout from '../../util/logout';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import useInterval from '../../hooks/useInterval';
import isValidOverride from '../../outages/isValidOverride';

/**
 * @name getTimeoutTime
 * @desc Gets the time (localized) when the user will be logged out due to inactivity.
 * @param {Number} remainingTime The number of milliseconds left before the user will be logged out.
 * @param {String} locale The user's locale
 */
const getTimeoutTime = (remainingTime, locale) => {
  const numberOfMlSeconds = Date.now();
  return new Date(numberOfMlSeconds + remainingTime)
    .toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) // display time in user's locale
    .replace(/^0+/, ''); // remove leading 0s in front of time
};

/* Used for refreshing the Okta access token, and to log users out if there's an outage */
const Timer = ({ currentOutage, locale, setUserLogin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { authState, oktaAuth } = useOktaAuth();

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const accessTokenExpiresAt = authState?.accessToken?.expiresAt;
  const validOverride = isValidOverride();

  useEffect(() => {
    const renewAccessToken = accessToken => {
      setGlobalHeader('Authorization', `Bearer ${accessToken}`);
      document.cookie = `NikeOktaToken=${accessToken};domain=.nike.net;path=/`;
      setUserLogin(accessToken);
    };

    /* refresh access token when it expires after 1 hour */
    oktaAuth.tokenManager.on('renewed', (key, newToken) => {
      if (key === 'accessToken' && newToken.accessToken) {
        renewAccessToken(newToken.accessToken);
      }
    });
  }, []);

  useEffect(() => {
    /* Logout the user if there's an outage */
    if (!validOverride && currentOutage) {
      logout(oktaAuth);
    }
  }, [validOverride, JSON.stringify(currentOutage)]);

  useInterval(() => {
    /* Logout the user if the access token is expired */
    const currentTime = Math.floor(Date.now() / ONE_SECOND); // convert to seconds

    if (currentTime >= accessTokenExpiresAt) {
      logout(oktaAuth);
    }
  }, ONE_SECOND * 3);

  const onIdle = async () => {
    logout(oktaAuth);
  };

  const onPrompt = async () => {
    const isAuthenticated = await oktaAuth.isAuthenticated();

    if (isAuthenticated) {
      openModal();
    }
  };

  const { reset, getRemainingTime } = useIdleTimer({
    timeout: ONE_MINUTE * 50,
    promptTimeout: ONE_MINUTE * 10,
    onIdle,
    onPrompt,
    startOnMount: false
  });

  const continueSession = async () => {
    closeModal();
    await oktaAuth.tokenManager.renew('accessToken');
    await oktaAuth.tokenManager.renew('idToken');
    reset();
  };

  const actions = (
    <ModalActionsStyled>
      <ButtonStyled onClick={continueSession}>
        {new TranslatedCopy({ id: 'global.okta.continueSession' }).toString()}
      </ButtonStyled>
    </ModalActionsStyled>
  );

  return (
    <ModalStyled
      isOpen={isOpen}
      closeModal={continueSession}
      title={`${new TranslatedCopy({ id: 'global.okta.continueSession' }).toString()}?`}
      slots={{ FooterSlot: actions }}
      shouldCloseOnBackgroundClick={false}
    >
      {new TranslatedCopy({
        id: 'global.okta.timeoutMessage',
        values: { time: getTimeoutTime(getRemainingTime(), locale) }
      }).toString()}
    </ModalStyled>
  );
};

const mapStateToProps = state => {
  return {
    currentOutage: currentOutage(state),
    locale: getUserData(state)?.locale?.replace('_', '-')
  };
};

Timer.propTypes = {
  currentOutage: object,
  locale: string,
  setUserLogin: func.isRequired
};

Timer.defaultProps = {
  currentOutage: null,
  locale: 'en-US'
};

export default connect(mapStateToProps, { setUserLogin })(Timer);
